import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import {
  Settings as SettingsIcon,
  Key,
  Globe,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Trash2,
  ExternalLink,
  Users,
  Bell,
  User,
} from 'lucide-react'
import ManageChildren from './ManageChildren'

type Section = 'canvas' | 'children' | 'notifications'

interface CanvasSettings {
  canvas_api_url: string | null
  canvas_api_key_masked: string | null
  canvas_credentials_verified: boolean
  canvas_credentials_updated_at: string | null
  has_credentials: boolean
}

interface TestResult {
  success: boolean
  message: string
  canvas_credentials_verified: boolean
}

interface ManualSyncResult {
  student_id: number
  student_name: string
  status: 'success' | 'error'
  grades_captured?: number
  assignments_synced?: number
  calendar_events_synced?: number
  announcements_synced?: number
  error?: string
}

interface ManualSyncResponse {
  success: boolean
  message: string
  students_synced: number
  students_total: number
  results: ManualSyncResult[]
  synced_at: string
  messages?: {
    status: 'success' | 'error'
    inbox_count?: number
    unread_count?: number
    error?: string
  }
}

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const sectionParam = searchParams.get('section') as Section | null
  const [activeSection, setActiveSection] = useState<Section>(sectionParam || 'canvas')

  const handleSectionChange = (section: Section) => {
    setActiveSection(section)
    setSearchParams(section === 'canvas' ? {} : { section })
  }

  const sections = [
    { id: 'canvas' as Section, label: 'Canvas', icon: Globe },
    { id: 'children' as Section, label: 'Children', icon: Users },
    { id: 'notifications' as Section, label: 'Notifications', icon: Bell },
  ]

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-8 h-8 text-quest-primary" />
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-gray-400">Manage your account and preferences</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex bg-surface-card rounded-lg p-1 gap-1">
        {sections.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => handleSectionChange(id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
              activeSection === id
                ? 'bg-quest-primary text-white'
                : 'text-gray-400 hover:text-white hover:bg-surface-hover'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Section Content */}
      {activeSection === 'canvas' && <CanvasSection />}
      {activeSection === 'children' && <ManageChildren />}
      {activeSection === 'notifications' && <NotificationsSection />}
    </div>
  )
}

// ── Canvas Connection Section ────────────────────────────
function CanvasSection() {
  const queryClient = useQueryClient()
  const [canvasUrl, setCanvasUrl] = useState('')
  const [canvasKey, setCanvasKey] = useState('')
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [syncResult, setSyncResult] = useState<ManualSyncResponse | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { data: settings, isLoading } = useQuery<CanvasSettings>({
    queryKey: ['canvas-settings'],
    queryFn: async () => {
      const response = await api.get('/api/parent/settings/canvas')
      return response.data
    },
  })

  useEffect(() => {
    if (settings?.canvas_api_url) {
      setCanvasUrl(settings.canvas_api_url)
    }
  }, [settings])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put('/api/parent/settings/canvas', {
        canvas_api_url: canvasUrl,
        canvas_api_key: canvasKey,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvas-settings'] })
      setCanvasKey('')
      setTestResult(null)
    },
  })

  const testMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/parent/settings/canvas/test')
      return response.data as TestResult
    },
    onSuccess: (data) => {
      setTestResult(data)
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['canvas-settings'] })
      }
    },
  })

  const syncNowMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/parent/settings/canvas/sync-now')
      return response.data as ManualSyncResponse
    },
    onSuccess: (data) => {
      setSyncResult(data)
      queryClient.invalidateQueries({ queryKey: ['canvas-settings'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete('/api/parent/settings/canvas')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvas-settings'] })
      setCanvasUrl('')
      setCanvasKey('')
      setTestResult(null)
      setSyncResult(null)
      setShowDeleteConfirm(false)
    },
  })

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    saveMutation.mutate()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-quest-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Canvas Credentials Card */}
      <div className="bg-surface-card rounded-xl p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Key className="w-5 h-5" />
            Canvas API Credentials
          </h2>
          {settings?.canvas_credentials_verified && (
            <span className="flex items-center gap-1 text-sm text-green-400">
              <CheckCircle className="w-4 h-4" />
              Verified
            </span>
          )}
        </div>

        {settings?.has_credentials && (
          <div className="bg-gray-700/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Globe className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400">URL:</span>
              <span>{settings.canvas_api_url}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Key className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400">API Key:</span>
              <span className="font-mono">{settings.canvas_api_key_masked}</span>
            </div>
            {settings.canvas_credentials_updated_at && (
              <div className="text-xs text-gray-500">
                Last updated: {new Date(settings.canvas_credentials_updated_at).toLocaleString()}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Canvas URL</label>
            <input
              type="url"
              value={canvasUrl}
              onChange={(e) => setCanvasUrl(e.target.value)}
              placeholder="https://yourschool.instructure.com"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-quest-primary"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              The URL of your school's Canvas instance (must use HTTPS)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">API Access Token</label>
            <input
              type="password"
              value={canvasKey}
              onChange={(e) => setCanvasKey(e.target.value)}
              placeholder={settings?.has_credentials ? 'Enter new key to update' : 'Paste your API token here'}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-quest-primary font-mono"
              required={!settings?.has_credentials}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saveMutation.isPending || (!canvasKey && !settings?.has_credentials)}
              className="px-4 py-2 bg-quest-primary text-white rounded-lg hover:bg-quest-primary/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {settings?.has_credentials ? 'Update Credentials' : 'Save Credentials'}
            </button>

            {settings?.has_credentials && (
              <button
                type="button"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {testMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Test Connection
              </button>
            )}

            {settings?.has_credentials && (
              <button
                type="button"
                onClick={() => syncNowMutation.mutate()}
                disabled={syncNowMutation.isPending}
                className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {syncNowMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Sync Now
              </button>
            )}
          </div>

          {saveMutation.isError && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <XCircle className="w-4 h-4" />
              {(saveMutation.error as Error)?.message || 'Failed to save credentials'}
            </div>
          )}

          {saveMutation.isSuccess && (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              Credentials saved. Click "Test Connection" to verify.
            </div>
          )}

          {syncNowMutation.isError && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <XCircle className="w-4 h-4" />
              {(syncNowMutation.error as Error)?.message || 'Manual sync failed'}
            </div>
          )}
        </form>

        {testResult && (
          <div className={`rounded-lg p-4 ${testResult.success ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
            <div className="flex items-start gap-3">
              {testResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                  {testResult.success ? 'Connection Successful!' : 'Connection Failed'}
                </p>
                <p className="text-sm text-gray-300 mt-1">{testResult.message}</p>
              </div>
            </div>
          </div>
        )}

        {syncResult && (
          <div className={`rounded-lg p-4 ${syncResult.success ? 'bg-blue-900/30 border border-blue-700' : 'bg-red-900/30 border border-red-700'}`}>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {syncResult.success ? (
                  <CheckCircle className="w-5 h-5 text-blue-300 flex-shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-300 flex-shrink-0" />
                )}
                <p className={syncResult.success ? 'text-blue-200' : 'text-red-200'}>
                  {syncResult.message}
                </p>
              </div>
              <p className="text-xs text-gray-300">
                Synced at: {new Date(syncResult.synced_at).toLocaleString()}
              </p>
              {syncResult.messages?.status === 'success' && (
                <p className="text-xs text-gray-300">
                  Messages refreshed: {syncResult.messages.inbox_count} inbox, {syncResult.messages.unread_count} unread
                </p>
              )}
              {syncResult.messages?.status === 'error' && (
                <p className="text-xs text-red-300">
                  Messages refresh failed: {syncResult.messages.error}
                </p>
              )}
              <div className="space-y-1 pt-1">
                {syncResult.results.map((row) => (
                  <div key={row.student_id} className="text-xs text-gray-200">
                    {row.student_name}: {row.status === 'success'
                      ? `grades ${row.grades_captured || 0}, assignments ${row.assignments_synced || 0}, calendar ${row.calendar_events_synced || 0}`
                      : (row.error || 'Sync failed')}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {settings?.has_credentials && (
          <div className="border-t border-gray-700/50 pt-4">
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-400 hover:text-red-300 text-sm flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Remove Canvas Credentials
              </button>
            ) : (
              <div className="bg-red-900/20 rounded-lg p-4 space-y-3">
                <p className="text-sm text-red-300">
                  Are you sure? The system will fall back to using default credentials.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm flex items-center gap-2"
                  >
                    {deleteMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                    Yes, Remove
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instructions Card */}
      <div className="bg-surface-card rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-blue-400" />
          How to Get Your Canvas API Token
        </h2>

        <ol className="list-decimal list-inside space-y-3 text-gray-300 text-sm">
          <li>Log in to your Canvas account at your school's Canvas website</li>
          <li>Click on <strong>Account</strong> in the left sidebar, then <strong>Settings</strong></li>
          <li>Scroll down to <strong>Approved Integrations</strong> section</li>
          <li>Click <strong>+ New Access Token</strong></li>
          <li>Enter a purpose (e.g., "Quest Parent Portal") and optionally set an expiration date</li>
          <li>Click <strong>Generate Token</strong> and copy the token shown</li>
          <li>Paste the token in the API Access Token field above</li>
        </ol>

        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-3 text-sm text-yellow-200">
          <strong>Important:</strong> Keep your API token secure. It provides access to your Canvas account.
          You can revoke it anytime from Canvas Settings if needed.
        </div>

        <a
          href="https://community.canvaslms.com/t5/Student-Guide/How-do-I-manage-API-access-tokens-as-a-student/ta-p/273"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-quest-primary hover:underline text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          Canvas Help: Managing API Access Tokens
        </a>
      </div>
    </div>
  )
}

// ── Notifications Section (placeholder) ──────────────────
function NotificationsSection() {
  return (
    <div className="space-y-6">
      <div className="bg-surface-card rounded-xl p-6">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-yellow-400" />
          Email Reports
        </h2>
        <p className="text-gray-400 text-sm mb-6">
          Configure when and how you receive homework reports and notifications.
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
            <div>
              <p className="font-medium">Daily Homework Report</p>
              <p className="text-sm text-gray-400">Email summary of each day's graded homework</p>
            </div>
            <span className="px-3 py-1 bg-gray-600 rounded-lg text-sm text-gray-400">Coming Soon</span>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
            <div>
              <p className="font-medium">Weekly Progress Digest</p>
              <p className="text-sm text-gray-400">Weekly overview of grades, homework, and game progress</p>
            </div>
            <span className="px-3 py-1 bg-gray-600 rounded-lg text-sm text-gray-400">Coming Soon</span>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
            <div>
              <p className="font-medium">Immediate Alerts</p>
              <p className="text-sm text-gray-400">Get notified about missing assignments, low grades, and new announcements</p>
            </div>
            <span className="px-3 py-1 bg-gray-600 rounded-lg text-sm text-gray-400">Coming Soon</span>
          </div>
        </div>
      </div>

      <div className="bg-surface-card rounded-xl p-6">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-blue-400" />
          Account
        </h2>
        <p className="text-gray-400 text-sm mb-4">
          Account management features are coming soon.
        </p>
        <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
          <div>
            <p className="font-medium">Change Password</p>
            <p className="text-sm text-gray-400">Update your login password</p>
          </div>
          <span className="px-3 py-1 bg-gray-600 rounded-lg text-sm text-gray-400">Coming Soon</span>
        </div>
      </div>
    </div>
  )
}
